import * as yup from "yup";
import YupPassword from "yup-password";
import validator from "validator";
import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LottieAnimation from "../../../animation/LottieAnimation";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  Stack,
  Link,
  Typography,
  useTheme,
  useMediaQuery,
  Grid2 as Grid,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Fade,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import EmailDrawer from "./EmailDrawer";
import { LoadingButton } from "@mui/lab";
import AdminLoginModal from "./AdminLoginModal";
import { privateProviderLicenseTypes } from "../../../data/privateProvider";
import ReCAPTCHA from "react-google-recaptcha";
import {
  trackRegistrationStart,
  trackRegistrationStep,
  trackRegistrationSubmit,
  trackRegistrationError,
  trackSignUp,
  setUserId,
} from "../../../analytics/gtag";
YupPassword(yup);

const NewRegister = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));

  // Only track analytics on production domain
  const isProduction = process.env.REACT_APP_DOMAIN === "permitrockstar.app";

  const [selectedPlan, setSelectedPlan] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isEmailError, setIsEmailError] = useState(false);
  const [isPhoneError, setIsPhoneError] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [licenseInputError, setLicenseInputError] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [verificationPolling, setVerificationPolling] = useState(false);
  const [verificationExpired, setVerificationExpired] = useState(false);
  const [verificationStatusMessage, setVerificationStatusMessage] =
    useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tempLicenseNumber, setTempLicenseNumber] = useState("");
  const [tempFirstName, setTempFirstName] = useState("");
  const [tempLastName, setTempLastName] = useState("");

  const [markedAdmin, setMarkedAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminSecret, setAdminSecret] = useState("");
  const [adminModalOpen, setAdminModalOpen] = useState(false);

  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoName, setPromoName] = useState("");
  const [promoError, setPromoError] = useState("");
  const [applyDiscountLoading, setApplyDiscountLoading] = useState(false);
  const [promoToken, setPromoToken] = useState("");

  const [recaptchaValue, setRecaptchaValue] = useState("");
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState("");
  // const [recaptchaTimestamp, setRecaptchaTimestamp] = useState(null);
  const [recaptchaKey, setRecaptchaKey] = useState(0);
  const recaptchaRef = useRef(null);

  const fetchPricingRef = useRef();

  // Demo mode detection
  const queryParams = new URLSearchParams(location.search);
  const isDemoMode = queryParams.get("demo") === "true";

  // Dynamic validation schema based on demo mode
  const validationSchema = useMemo(() => {
    return yup.object({
      firstName: yup
        .string()
        .min(3, "First name must be at least 3 characters")
        .matches(
          /^[A-Za-z\s]+$/,
          "First name cannot contain numbers or special characters",
        )
        .required("First name is required"),
      lastName: yup
        .string()
        .min(3, "Last name must be at least 3 characters")
        .matches(
          /^[A-Za-z\s]+$/,
          "Last name cannot contain numbers or special characters",
        )
        .required("Last name is required"),
      email: yup.string().email("Must be a valid email").required(),
      ppLicenseType: isDemoMode
        ? yup.string()
        : selectedPlan?.title === "Private Provider"
          ? yup.string().required("License type is required")
          : yup.string(),
      phone: yup
        .string()
        .required("Phone number is required")
        .test("PhoneTest", "Phone number is invalid", function (value) {
          const isValidPhoneNumber = validator.isMobilePhone(value);
          if (isValidPhoneNumber) {
            return true;
          } else {
            return false;
          }
        }),
      password: yup
        .string()
        .min(8, "Password must contain 8 or more characters")
        .max(255)
        .minLowercase(1, "Password must contain at least 1 lower case letter")
        .minUppercase(1, "Password must contain at least 1 upper case letter")
        .minNumbers(1, "Password must contain at least 1 number")
        .minSymbols(1, "Password must contain at least 1 special character")
        .required("Password is required"),
      confirmPassword: yup
        .string()
        .required("Password confirmation is required")
        .test("Passwords-match", "Passwords must match", function (value) {
          return this.parent.password === value;
        }),
      licenseValidated: isDemoMode
        ? yup.boolean()
        : selectedPlan?.title === "Free"
          ? yup.boolean().required()
          : yup.boolean(),
      licenseNumber: isDemoMode
        ? yup.string()
        : selectedPlan?.title === "Free"
          ? yup.string().min(7, "").max(12, "").required("")
          : selectedPlan?.title === "Private Provider"
            ? yup.string().min(5, "").max(10, "").required("")
            : yup.string().min(7, "").max(12, ""),
      emailConsent: isDemoMode
        ? yup.boolean()
        : yup.boolean().oneOf([true], "You must accept email verification."),
      smsConsent: yup.boolean(),
    });
  }, [isDemoMode, selectedPlan?.title]);

  const toCamelCase = (str) =>
    str
      .toLowerCase()
      .replace(/(?:^\w|[ ]\w)/g, (match, index) =>
        index === 0 ? match.toLowerCase() : match.trim().toUpperCase(),
      );

  const validatePromoCode = async () => {
    try {
      setApplyDiscountLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/validate-promo-code`,
        {
          promoCode,
          plan_name: selectedPlan?.title,
        },
        { withCredentials: true },
      );
      if (resp?.data?.success) {
        setPromoName(resp?.data?.name);
        setPromoToken(resp?.data?.token);
        setPromoError("");
        if (isProduction) {
          trackRegistrationStep({ step: "promo_applied" });
        }
      } else {
        setPromoName("");
        setPromoError(resp?.data?.message);
      }
    } catch (error) {
      setPromoName("");
      setPromoError("Promo code is invalid");
    } finally {
      setApplyDiscountLoading(false);
    }
  };

  useEffect(() => {
    if (
      (adminEmail === "" || adminSecret === "") &&
      markedAdmin === true &&
      adminModalOpen === false
    ) {
      setAdminModalOpen(true);
    }
  }, [adminEmail, adminSecret, markedAdmin, adminModalOpen]);

  useEffect(() => {
    const getTiers = async () => {
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/nocApi/data/tiers`,
        );

        if (resp.data.success) {
          const tierTitlesRaw = resp.data.data.map((tier) =>
            tier.title.toLowerCase(),
          );

          const tierTitles = tierTitlesRaw.map(toCamelCase);

          const queryParams = new URLSearchParams(location.search);
          const typeParam = queryParams.get("type");
          const adminParam = queryParams.get("admin");

          if (adminParam && adminParam === "true") {
            setMarkedAdmin(true);
          }

          if (!tierTitles.includes(typeParam)) {
            window.location.href = "https://permitrockstar.com/pricing/";
            return;
          }

          resp.data.data.forEach((tier) => {
            const tierTitle = toCamelCase(tier.title);
            if (tierTitle === typeParam) {
              const planObj = {
                title: tier?.title,
                product_id: tier?.product_id,
                price_id: tier?.monthly_price_id,
                license: parseInt(tier?.license),
                subuser: parseInt(tier?.subuser),
                noc: tier?.noc,
                county: tier?.county,
                interval: "month",
              };
              setSelectedPlan(planObj);
            }
          });
        }
      } catch (e) {
        console.error(e);
      }
    };

    if (!fetchPricingRef?.current) {
      fetchPricingRef.current = true;
      getTiers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPlan?.title) {
      setPageLoading(false);
      // Track registration start when user lands on registration page
      if (isProduction) {
        trackRegistrationStart({ method: "email" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan]);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      licenseNumber: "",
      ppLicenseType: "",
      licenseValidated: false,
      emailConsent: false,
      smsConsent: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, helpers) => {
      try {
        setSubmitLoading(true);
        if (
          !isDemoMode &&
          selectedPlan?.title === "Free" &&
          !values.licenseValidated
        ) {
          return;
        }

        values["plan"] = selectedPlan;
        values["terms"] = true;
        values["timezone"] =
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          "America/New_York";
        values["markedAdmin"] = markedAdmin;
        values["adminEmail"] = adminEmail;
        values["adminSecret"] = adminSecret;
        values["promoToken"] = promoToken;
        values["promoCode"] = promoCode;

        // Ensure reCAPTCHA value is valid before sending
        if (
          !recaptchaValue ||
          typeof recaptchaValue !== "string" ||
          recaptchaValue.trim() === ""
        ) {
          setRecaptchaError(
            "reCAPTCHA verification is required. Please verify again.",
          );
          setSubmitLoading(false);
          return;
        }

        values["recaptchaValue"] = recaptchaValue;

        const createUserResp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/auth/createUser`,
          {
            values,
          },
          { withCredentials: true },
        );

        // Track successful user creation
        if (isProduction) {
          if (createUserResp?.data?.user?.id) {
            setUserId(createUserResp.data.user.id);
          }
          trackSignUp({
            method: "email",
            plan: selectedPlan?.title || "unknown",
          });

          // Clear registration session ID after successful sign up
          try {
            sessionStorage.removeItem("pr_reg_session_id");
          } catch {}
        }

        try {
          const stripe_resp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/create-checkout-session`,
            {
              values,
            },
            { withCredentials: true },
          );
          if (
            stripe_resp?.data?.success &&
            stripe_resp?.data?.free &&
            stripe_resp?.data?.token
          ) {
            navigate(
              "/2fa-secure?token=" +
                stripe_resp.data.token +
                "&sms=" +
                values?.smsConsent,
            );
            return;
          } else {
            window.location.href = stripe_resp.data;
          }
        } catch (e) {
          if (
            e.response &&
            e.response.data &&
            e.response.data.message === "Payment session canceled"
          ) {
          } else {
          }
        }
      } catch (err) {
        // Handle reCAPTCHA validation errors
        if (
          err.response &&
          err.response.data &&
          (err.response.data.message === "Invalid reCAPTCHA verification" ||
            err.response.data.message ===
              "Invalid reCAPTCHA verification. Please try again.")
        ) {
          setRecaptchaError(
            "reCAPTCHA verification failed. Please verify again.",
          );
          setRecaptchaValue("");
          setRecaptchaVerified(false);
          // Reset the reCAPTCHA widget
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
        }
        // setErrorText(err.response.data.message);
      } finally {
        setSubmitLoading(false);
      }
    },
  });

  const sendCode = async (emailValue, useLink = true, forceNew = false) => {
    // TEMPORARILY DISABLED: Skip generating and sending email verification codes.
    // This short-circuits the flow so users can continue registering without verification.
    // return {
    //   success: true,
    //   message: "Email verification temporarily disabled",
    //   isEmailSent: false,
    //   isVerified: true,
    // };

    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/email/generateverify`,
        {
          userEmail: emailValue,
          useLink: useLink, // Use link-based verification for registration
          forceNew: forceNew, // Force new verification (clear existing)
        },
        { withCredentials: true },
      );

      return {
        success: resp.data.success,
        message: resp.data.message,
        isEmailSent: resp.data.isEmailSent,
        isVerified: resp.data.isVerified,
      };
    } catch (e) {
      if (e.response && e.response.data) {
        return {
          success: false,
          message: e.response.data.message,
        };
      } else {
        return { success: false, message: "Server is not working" };
      }
    }

    /// SEND EMAIL API WITH CODE ////
    /////////////////////////////////
  };

  // State to track if email is verified and ready for reCAPTCHA
  const [emailVerified, setEmailVerified] = useState(false);
  const [modalSubmitLoading, setModalSubmitLoading] = useState(false);

  // Store email for polling (to avoid dependency on formik object)
  const pollingEmailRef = useRef("");

  // Poll for email verification status by checking Redis
  useEffect(() => {
    if (!verificationPolling) {
      return;
    }

    // Capture email at the start of polling
    pollingEmailRef.current = formik.values.email;
    const userType = new URLSearchParams(location.search).get("type");
    const pollingStartedAt = Date.now();
    const maxPollingMs = 30 * 60 * 1000; // 30 minutes
    let pollAttempts = 0;
    const maxPollAttempts = 600; // ~30 minutes at 3s intervals

    const checkVerification = async () => {
      pollAttempts += 1;

      if (
        Date.now() - pollingStartedAt > maxPollingMs ||
        pollAttempts > maxPollAttempts
      ) {
        setVerificationPolling(false);
        setVerificationExpired(true);
        setVerificationStatusMessage(
          "Verification expired. Please resend verification email.",
        );
        return;
      }

      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/email/checkverify`,
          {
            code: "",
            emailAddress: pollingEmailRef.current,
            userType: userType,
            useLink: true,
          },
          { withCredentials: true },
        );

        if (resp.data.success && resp.data.isVerified) {
          setVerificationPolling(false);
          setVerificationExpired(false);
          setVerificationStatusMessage("");
          setEmailVerified(true);
          // Reset reCAPTCHA for fresh verification
          setRecaptchaValue("");
          setRecaptchaVerified(false);
          // setRecaptchaTimestamp(null);
          setRecaptchaError("");
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
        }
      } catch (e) {
        if (e?.response?.status === 401) {
          setVerificationPolling(false);
          setVerificationExpired(true);
          setVerificationStatusMessage(
            e?.response?.data?.message ||
              "Verification expired. Please resend verification email.",
          );
        }
      }
    };

    // Run immediately, then every 3 seconds
    checkVerification();
    const interval = setInterval(checkVerification, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [verificationPolling, location.search, formik.values.email]);

  // Reset recaptcha whenever emailVerified becomes true (after refresh or verification)
  useEffect(() => {
    if (emailVerified) {
      // Reset reCAPTCHA state to ensure fresh verification
      setRecaptchaValue("");
      setRecaptchaVerified(false);
      // setRecaptchaTimestamp(null);
      setRecaptchaError("");
      // Force remount of recaptcha widget by changing key
      setRecaptchaKey((prev) => prev + 1);
      // Reset the reCAPTCHA widget if it exists
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.reset();
        } catch (_) {
          // Widget might not be ready yet, that's okay
        }
      }
    }
  }, [emailVerified]);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCountdown <= 0) return;

    const timer = setInterval(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCountdown]);

  // ?  PRE-SUBMITTING FORM
  const handlePreSubmit = async () => {
    if (submitLoading) {
      return;
    }

    let handedOffToFormik = false;

    try {
      setSubmitLoading(true);
      // Track registration submission attempt with enriched context
      if (isProduction) {
        trackRegistrationSubmit({
          method: "email",
          extras: {
            plan: selectedPlan?.title || "unknown",
            admin: markedAdmin ? "true" : "false",
            has_promo: promoName ? "true" : "false",
          },
        });
      }
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        // Track validation errors
        if (isProduction) {
          trackRegistrationError({
            code: "validation_failed",
            fields: Object.keys(errors),
          });
        }
        // Iterate over the errors to set each error and mark each field as touched
        Object.entries(errors).forEach(([field, errorMessage]) => {
          formik.setFieldError(field, errorMessage);
          formik.setFieldTouched(field, true, false); // Marks the field as touched
          if (field === "licenseNumber") {
            setLicenseInputError(true);

            const minValue =
              selectedPlan?.title === "Private Provider" ? "5" : "7";
            const maxValue =
              selectedPlan?.title === "Private Provider" ? "10" : "12";
            setValidationError(
              `License number must be between ${minValue} and ${maxValue} characters`,
            );
          }
        });
        setSubmitLoading(false);
        return;
      }
      if (
        !isDemoMode &&
        selectedPlan?.title !== "Private Provider" &&
        (selectedPlan?.title === "Free" ||
          formik.values.licenseNumber !== "") &&
        (!formik.values.licenseValidated ||
          formik.values.licenseNumber !== tempLicenseNumber ||
          formik.values.firstName !== tempFirstName ||
          formik.values.lastName !== tempLastName)
      ) {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/data/validate-registering-license`,
          {
            license_number: formik.values.licenseNumber,
            first_name: formik.values.firstName,
            last_name: formik.values.lastName,
          },
        );
        if (resp.data.success === true) {
          setLicenseInputError(false);
          formik.setFieldValue("licenseValidated", true);
          setTempLicenseNumber(formik.values.licenseNumber);
          setTempFirstName(formik.values.firstName);
          setTempLastName(formik.values.lastName);
          setValidationError("");
          if (isProduction) {
            trackRegistrationStep({ step: "license_validated" });
          }
        } else {
          if (
            selectedPlan?.title === "Free" ||
            formik.values.licenseNumber !== ""
          ) {
            setValidationError(resp.data.message);
            setTempLicenseNumber("");
            setTempFirstName("");
            setTempLastName("");
            formik.setFieldValue("licenseValidated", false);
            if (isProduction) {
              trackRegistrationError({
                code: "license_mismatch",
                fields: ["licenseNumber"],
              });
            }
          }
          return;
        }
      }

      // CHECK EMAIL FOR DUPLICATIONS //
      const queryParams = new URLSearchParams(location.search);
      const typeParam = queryParams.get("type");

      // Skip email verification in demo mode
      if (isDemoMode) {
        setIsEmailError(false);
        setIsPhoneError(false);
        formik.setTouched({});
        handedOffToFormik = true;
        await formik.submitForm();
        return;
      }

      const emailResp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/email/verifyEmail`,
        {
          emailAddress: formik.values.email,
          phone: formik.values.phone,
          userType: typeParam,
        },
        { withCredentials: true },
      );

      if (emailResp.data.success === true) {
        if (
          emailResp.data.data.userExist === true ||
          emailResp.data.data.phoneExist === true
        ) {
          const errors = {};
          const touched = {};
          const errorFields = [];

          if (emailResp.data.data.userExist === true) {
            setIsEmailError(true);
            errors.email = "Email already exists.";
            touched.email = true;
            errorFields.push("email");
          }

          if (emailResp.data.data.phoneExist === true) {
            setIsPhoneError(true);
            errors.phone = "Phone number already exists.";
            touched.phone = true;
            errorFields.push("phone");
          }

          // Track duplicate contact error
          if (isProduction) {
            trackRegistrationError({
              code: "duplicate_contact",
              fields: errorFields,
            });
          }

          formik.setErrors(errors);
          formik.setTouched(touched);

          // Clear values only for the fields that have issues
          formik.setValues({
            ...formik.values,
            email: errors.email ? "" : formik.values.email,
            phone: errors.phone ? "" : formik.values.phone,
          });

          setSubmitLoading(false);
        } else {
          setIsEmailError(false);
          setIsPhoneError(false);

          const sendCodeValue = await sendCode(formik.values.email, true, true); // Use link-based verification, force new
          if (sendCodeValue.success === false) {
            setSubmitLoading(false);
          } else {
            if (sendCodeValue.isVerified === true) {
              // Email already verified - show modal with reCAPTCHA
              if (isProduction) {
                trackRegistrationStep({ step: "email_verified" });
              }
              setEmailSent(true);
              setVerificationExpired(false);
              setVerificationStatusMessage("");
              setEmailVerified(true); // Skip waiting, show reCAPTCHA directly
              setVerificationPolling(false);
              setSubmitLoading(false);
            } else {
              if (isProduction) {
                trackRegistrationStep({ step: "email_verification" });
              }
              // Show email sent message and wait for verification
              setEmailSent(true);
              setVerificationExpired(false);
              setVerificationStatusMessage("");
              setEmailVerified(false);
              setVerificationPolling(true);
              setSubmitLoading(false);
            }
          }
        }
      }
    } catch (e) {
      if (
        (selectedPlan?.title === "Free" ||
          formik.values.licenseNumber !== "") &&
        e.response &&
        e?.response?.data?.type === "license"
      ) {
        setValidationError(e?.response?.data?.message);
        setLicenseInputError(true);
        setTempLicenseNumber("");
        setTempFirstName("");
        setTempLastName("");
        formik.setFieldValue("licenseValidated", false);
        if (isProduction) {
          trackRegistrationError({
            code: "license_validation_error",
            fields: ["licenseNumber"],
          });
        }
      }
      if (e.response && e?.response?.data?.type !== "license") {
        // setErrorText(e?.response?.data?.message);
      }
    } finally {
      if (!handedOffToFormik) {
        setSubmitLoading(false);
      }
    }
  };

  const handleLicenseNumberInputChange = (event) => {
    const value = event?.target.value.toUpperCase();
    setValidationError("");

    formik?.setFieldValue("licenseNumber", value);

    // Skip validation in demo mode
    if (isDemoMode) {
      setLicenseInputError(false);
      return;
    }

    const minLength = selectedPlan?.title === "Private Provider" ? 5 : 7;
    const maxLength = selectedPlan?.title === "Private Provider" ? 10 : 12;

    if (value.length >= minLength && value.length <= maxLength) {
      setLicenseInputError(false);
    } else {
      setLicenseInputError(true);
      setValidationError(
        `License number must be between ${minLength} and ${maxLength} characters`,
      );
    }
  };

  const handleNameChange = (e) => {
    const { name, value } = e.target;

    const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
    formik.setFieldValue(name, formattedValue);
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;

    const formattedValue = value.toLowerCase();
    formik.setFieldValue(name, formattedValue);
  };

  const handleNumberInputChange = (event) => {
    if (event.target.value.length > 12) {
      return;
    }

    // Update Formik value
    formik.setFieldValue(event.target.name, event.target.value);
    if (isPhoneError) {
      setIsPhoneError(false);
    }
  };

  const handlePhoneInputChange = (event) => {
    const { value } = event.target;

    const digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters
    let formattedPhoneNumber = "";

    // Add dashes after 3rd and 6th digits
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    formik.setFieldValue("phone", formattedPhoneNumber);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleRecaptchaVerify = async (response) => {
    if (response) {
      setRecaptchaValue(response);
      setRecaptchaVerified(true);
      setRecaptchaError("");
      // setRecaptchaTimestamp(Date.now());
    } else {
      setRecaptchaValue("");
      setRecaptchaVerified(false);
    }
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaValue("");
    setRecaptchaVerified(false);
    // setRecaptchaTimestamp(null);
    setRecaptchaError("reCAPTCHA expired. Please verify again.");
  };

  useEffect(() => {
    if (
      (isDemoMode || selectedPlan?.title !== "Free") &&
      formik.values.licenseNumber === "" &&
      validationError !== ""
    ) {
      setValidationError("");
      setLicenseInputError(false);
      formik.setFieldError("licenseNumber", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.licenseNumber, isDemoMode]);

  return (
    <>
      {pageLoading ? (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Box>
            <img
              style={{
                width: "10rem",
                marginLeft: "auto",
                marginRight: "auto",
              }}
              alt="Permit Rockstar Logo"
              src="/img/website_img/PermitRockstar.png"
            />
          </Box>
          <LottieAnimation />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundImage: mobileSize
              ? "none"
              : "url('/img/website_img/homeImg.jpeg')",
            backgroundRepeat: "repeat",
            minHeight: "100vh",
            display: "flex",
            justifyContent: mobileSize ? undefined : "center",
            alignItems: "center",
            flexDirection: "column",
            overflow: mobileSize ? "hidden" : undefined,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              mt: 0,
              maxWidth: "1100px",
              borderRadius: "25px",
              px: mobileSize ? 1 : 3,
              py: mobileSize ? 1 : 2,
              width: mobileSize ? "100%" : "85%",
              overflow: "hidden",
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack spacing={1} sx={{ mb: 1 }}>
              <Box display="flex" justifyContent="space-between">
                <Link
                  sx={{ fontWeight: "bold", cursor: "pointer" }}
                  onClick={() => navigate("/login")}
                  underline="hover"
                  variant="subtitle2"
                  display="flex"
                  alignItems="center"
                >
                  <ArrowBackIcon sx={{ marginRight: 1 }} />
                  Back to login
                </Link>
                <div style={{ display: "flex", alignSelf: "center" }}>
                  <img
                    style={{
                      width: "8rem",
                      marginRight: "7.5rem",
                      marginTop: "-9px",
                    }}
                    alt="Permit Rockstar Logo"
                    src="/img/website_img/logoText.png"
                  />
                </div>

                <div />
              </Box>
              <Typography
                sx={{ textAlign: "center" }}
                color="#004976"
                variant="h5"
              >
                Enter Your Details to Sign Up{" "}
                {selectedPlan?.title === "Private Provider"
                  ? "for Private Provider Plan"
                  : ""}
                {isDemoMode && " (Demo Mode)"}
              </Typography>
            </Stack>

            <Grid
              container
              spacing={1}
              rowSpacing={1}
              sx={{
                height: mobileSize ? "calc(95vh - 11rem)" : "calc(80vh - 7rem)",
                overflowY: "auto",
                paddingRight: "2px",
                py: "2px",
                mt: 1,
                alignItems: "flex-start",
                flexGrow: 1,
                px: 1,
              }}
            >
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  error={
                    Boolean(formik.touched.firstName) &&
                    Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched?.firstName && formik.errors?.firstName
                  }
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    handleNameChange(e);
                  }}
                  value={formik.values.firstName}
                  name="firstName"
                  label="First Name*"
                  variant="filled"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  error={
                    Boolean(formik.touched.lastName) &&
                    Boolean(formik.errors.lastName)
                  }
                  helperText={
                    formik.touched?.lastName && formik.errors?.lastName
                  }
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    handleNameChange(e);
                  }}
                  value={formik.values.lastName}
                  name="lastName"
                  label="Last Name*"
                  variant="filled"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  error={
                    Boolean(formik.touched.email) &&
                    Boolean(formik.errors.email)
                  }
                  helperText={
                    isEmailError === true && !formik.isValid
                      ? "Email address already exists"
                      : formik.touched?.email && formik.errors?.email
                  }
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    handleEmailChange(e);
                  }}
                  value={formik.values.email}
                  name="email"
                  label="Email Address*"
                  variant="filled"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  error={
                    (isPhoneError && formik.touched.phone) || // Check phone validation error
                    (formik.touched.phone && Boolean(formik.errors.phone)) // Fall back to generic error
                  }
                  helperText={
                    isPhoneError === true && !formik.isValid
                      ? "Phone number already exists"
                      : formik.touched?.phone && formik.errors?.phone
                  }
                  onKeyUp={handlePhoneInputChange}
                  onBlur={formik.handleBlur}
                  onChange={handleNumberInputChange}
                  value={formik.values.phone}
                  name="phone"
                  label="Phone Number*"
                  variant="filled"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  error={
                    Boolean(formik.touched.password) &&
                    Boolean(formik.errors.password)
                  }
                  helperText={
                    formik.touched?.password && formik.errors?.password
                  }
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.password}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  label="Password*"
                  variant="filled"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
                          onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  error={
                    Boolean(formik.touched.confirmPassword) &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  helperText={
                    formik.touched?.confirmPassword &&
                    formik.errors?.confirmPassword
                  }
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.confirmPassword}
                  type="password"
                  name="confirmPassword"
                  label="Confirm Password*"
                  variant="filled"
                  fullWidth
                />
              </Grid>
              <Grid size={{ sm: 12, md: 6 }}>
                <Box
                  sx={{
                    mx: tabletSize ? undefined : "auto",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    mt={1}
                    color="primary"
                    align="center"
                  >
                    {isDemoMode
                      ? "License Number (Optional in Demo Mode)"
                      : selectedPlan?.title === "Free"
                        ? "Before proceeding, we will validate your license number"
                        : selectedPlan?.title === "Private Provider"
                          ? "To register, you'll need a valid Private Provider license"
                          : "To register, you'll need a valid contractor license"}
                  </Typography>
                  <Typography
                    variant="body2"
                    mt={1}
                    align="center"
                    fontWeight="bold"
                    mb={2}
                  >
                    {isDemoMode
                      ? "In demo mode, you can skip license validation and proceed without entering a license number."
                      : selectedPlan?.title === "Free"
                        ? "The registering user must hold a valid contractor license to proceed with registration."
                        : selectedPlan?.title === "Private Provider"
                          ? "Please ensure the license you enter is valid and accurate. This license will be used in your generated documents and cannot be changed later."
                          : " We recommend that the licensed contractor registers initially for license validation. You can add it later, but no permit packages can be created until the license is validated."}
                  </Typography>
                  {selectedPlan?.title === "Private Provider" &&
                    !isDemoMode && (
                      <FormControl
                        fullWidth
                        margin="normal"
                        variant="filled"
                        error={
                          formik?.touched?.ppLicenseType &&
                          Boolean(formik?.errors?.ppLicenseType)
                        }
                      >
                        <InputLabel>License Type</InputLabel>
                        <Select
                          name="ppLicenseType"
                          value={formik?.values?.ppLicenseType || ""}
                          onChange={formik?.handleChange}
                          onBlur={formik?.handleBlur}
                          error={
                            formik?.touched?.ppLicenseType &&
                            Boolean(formik?.errors?.ppLicenseType)
                          }
                          label="License Type"
                          MenuProps={{
                            PaperProps: {
                              style: {
                                marginTop: "1.5px",
                                maxHeight: 200,
                                width: "auto",
                                maxWidth: "100%",
                              },
                            },
                          }}
                        >
                          {privateProviderLicenseTypes?.map((option, index) => (
                            <MenuItem key={index} value={option?.code}>
                              {option?.label}
                            </MenuItem>
                          ))}
                        </Select>

                        {formik?.touched?.ppLicenseType &&
                          formik?.errors?.ppLicenseType && (
                            <FormHelperText sx={{ color: "red" }}>
                              {formik?.errors?.ppLicenseType}
                            </FormHelperText>
                          )}
                      </FormControl>
                    )}
                  <TextField
                    error={
                      Boolean(formik.touched.licenseNumber) &&
                      Boolean(formik.errors.licenseNumber)
                    }
                    helperText={
                      formik.touched?.licenseNumber &&
                      formik.errors?.licenseNumber
                    }
                    onBlur={formik.handleBlur}
                    onChange={(e) => handleLicenseNumberInputChange(e)}
                    value={formik.values.licenseNumber}
                    name="licenseNumber"
                    label={
                      isDemoMode
                        ? "License Number (Optional)"
                        : "License Number"
                    }
                    variant="filled"
                    fullWidth
                    className="fadeIn"
                    slotProps={{
                      htmlInput: {
                        style: {
                          fontSize: "28px",
                          fontWeight: "bold",
                          color: licenseInputError ? "red" : undefined,
                        },
                      },
                    }}
                    sx={
                      licenseInputError
                        ? {
                            mb: 1,
                            "& .MuiFilledInput-root": {
                              "&:before": {
                                borderBottomColor: "red",
                              },
                              "&:hover:not(.Mui-disabled):before": {
                                borderBottomColor: "red",
                              },
                              "&.Mui-focused:before": {
                                borderBottomColor: "red",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "red",
                            },
                          }
                        : { mb: 1 }
                    }
                  />
                  <Typography
                    align="center"
                    fontWeight="bold"
                    variant="body1"
                    color="error"
                    mb={0}
                    sx={{ minHeight: "1.4rem" }}
                  >
                    {validationError}
                  </Typography>
                </Box>
                {selectedPlan?.title !== "Private Provider" && (
                  <Box sx={{ textAlign: "center", mt: 0 }}>
                    {!showPromoInput ? (
                      <Typography
                        size="small"
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={() => setShowPromoInput(true)}
                      >
                        Have a Promo Code?
                      </Typography>
                    ) : !Boolean(promoName) && showPromoInput ? (
                      <>
                        <Fade in={showPromoInput}>
                          <Box
                            sx={{
                              mt: 0.5,
                              gap: 1,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <TextField
                              size="small"
                              variant="filled"
                              label="Enter Promo Code"
                              value={promoCode}
                              onChange={(e) => {
                                setPromoCode(e.target.value.toUpperCase());

                                if (promoError) {
                                  setPromoError("");
                                }
                              }}
                              sx={{ width: "100%", maxWidth: 300 }}
                              error={Boolean(promoError)}
                            />
                            <LoadingButton
                              loading={applyDiscountLoading}
                              disabled={
                                promoCode.trim() === "" ||
                                promoCode.trim().length < 3
                              }
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={validatePromoCode}
                            >
                              Apply
                            </LoadingButton>
                            {showPromoInput && (
                              <>
                                <Button
                                  disabled={applyDiscountLoading}
                                  onClick={() => {
                                    setShowPromoInput(false);
                                    setPromoName("");
                                    setPromoCode("");
                                    setPromoError("");
                                  }}
                                  size="small"
                                  variant="contained"
                                  color="error"
                                >
                                  Cancel
                                </Button>
                              </>
                            )}
                          </Box>
                        </Fade>
                        {Boolean(promoError) && (
                          <FormHelperText sx={{ ml: 0.5 }} error>
                            {promoError}
                          </FormHelperText>
                        )}
                      </>
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            color="primary"
                            align="center"
                          >
                            Promo Code Activated
                          </Typography>
                          <CheckCircleIcon color="success" />
                        </Box>

                        <Box
                          gap={1}
                          sx={{
                            border: "2px solid #004976",
                            boxShadow: "0px 0px 10px rgba(0,0,0,0.5)",
                            borderRadius: "15px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "primary.main",
                              fontWeight: "bold",
                            }}
                          >
                            {promoName}
                          </Typography>
                          <>
                            <IconButton
                              onClick={() => {
                                setShowPromoInput(false);
                                setPromoName("");
                                setPromoCode("");
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Grid>
              <Grid size={{ sm: 12, md: 6 }} mb={0}>
                <Box sx={{ borderBottom: "3px solid #d3d3d3" }} mb={1}>
                  <Typography
                    color="primary"
                    fontWeight="bold"
                    variant="subtitle1"
                  >
                    Email Verification{" "}
                    {isDemoMode ? "(Optional)" : "(Required)"}
                  </Typography>
                  <Typography variant="body2">
                    {isDemoMode
                      ? "In demo mode, email verification is optional. Your email will be used for account security, password resets, and essential notifications."
                      : "Your email will be used for account security, password resets, and essential notifications."}
                  </Typography>
                  {!isDemoMode && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="emailConsent"
                          checked={formik.values.emailConsent}
                          onChange={formik.handleChange}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I understand, that Permit Rockstar, requires
                          emailverification for security purposes.
                        </Typography>
                      }
                    />
                  )}
                  {!isDemoMode &&
                    formik.touched.emailConsent &&
                    formik.errors.emailConsent && (
                      <FormHelperText error>
                        {formik.errors.emailConsent}
                      </FormHelperText>
                    )}
                </Box>
                <Box>
                  <Typography
                    color="primary"
                    fontWeight="bold"
                    variant="subtitle1"
                  >
                    SMS Verification (Optional)
                  </Typography>
                  <Typography variant="body2">
                    A simple and secure way to protect your account with
                    Two-Factor Authentication (2FA).
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="smsConsent"
                        checked={formik.values.smsConsent}
                        onChange={formik.handleChange}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        By checking this box, you consent to receive 2FA, system
                        notifications, and sporadic marketing text messages from
                        Permit Rockstar. Message frequency may vary. Msg and
                        Data rates apply. Reply "STOP" to unsubscribe. Text HELP
                        for assistance. View{" "}
                        <Link
                          href="https://permitrockstar.com//terms-and-conditions/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms and Conditions
                        </Link>{" "}
                        and{" "}
                        <Link
                          href="https://permitrockstar.com/privacy-policy/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Privacy Policy
                        </Link>
                        .
                      </Typography>
                    }
                  />
                  {formik.touched.smsConsent && formik.errors.smsConsent && (
                    <FormHelperText error>
                      {formik.errors.smsConsent}
                    </FormHelperText>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                py: 2,
                backgroundColor: "#fff",
                display: "flex",
                justifyContent: "center",
                boxShadow: "0px -2px 10px rgba(133, 67, 67, 0.1)",
                mt: "auto",
                px: mobileSize ? 1 : undefined,
              }}
            >
              <LoadingButton
                disabled={!Boolean(promoName) && showPromoInput}
                variant="contained"
                sx={{ width: "20rem" }}
                loading={submitLoading}
                onClick={handlePreSubmit}
              >
                Submit
              </LoadingButton>
            </Box>
          </Box>
          <EmailDrawer
            open={emailDrawerOpen}
            setOpen={setEmailDrawerOpen}
            setSubmitLoading={setSubmitLoading}
            formik={formik}
          />

          {/* Email Verification Modal */}
          {emailSent && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1300,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "white",
                  padding: 4,
                  borderRadius: 2,
                  maxWidth: 500,
                  textAlign: "center",
                  mx: 2,
                }}
              >
                {!emailVerified ? (
                  // Waiting for email verification
                  <>
                    <CheckCircleIcon
                      sx={{ fontSize: 60, color: "success.main", mb: 2 }}
                    />
                    <Typography variant="h5" gutterBottom>
                      Email Sent!
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      We've sent a verification email to{" "}
                      <strong>{formik.values.email}</strong>. Please check your
                      email and click the verification link to continue.
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        verificationExpired ? "error.main" : "text.secondary"
                      }
                      sx={{ mb: 3 }}
                    >
                      {verificationExpired
                        ? verificationStatusMessage ||
                          "Verification expired. Please resend verification email."
                        : "Waiting for email verification..."}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <LoadingButton
                        variant="outlined"
                        color="primary"
                        loading={resendLoading}
                        disabled={resendCountdown > 0}
                        onClick={async () => {
                          setResendLoading(true);
                          try {
                            // Stop polling first, then restart after resend
                            setVerificationPolling(false);
                            const result = await sendCode(
                              formik.values.email,
                              true,
                              true,
                            ); // Force new token

                            if (result?.success) {
                              if (result?.isVerified) {
                                setVerificationExpired(false);
                                setVerificationStatusMessage("");
                                setEmailVerified(true);
                                setVerificationPolling(false);
                              } else {
                                setResendCountdown(120); // 2 minutes
                                setVerificationExpired(false);
                                setVerificationStatusMessage("");
                                // Reset verification state and restart polling for new token
                                setEmailVerified(false);
                                // Use setTimeout to ensure state update triggers new effect
                                setTimeout(() => {
                                  setVerificationPolling(true);
                                }, 100);
                              }
                            } else {
                              setVerificationExpired(true);
                              setVerificationStatusMessage(
                                result?.message ||
                                  "Failed to resend verification email. Please try again.",
                              );
                            }
                          } catch (e) {
                            console.error("Failed to resend email:", e);
                            setVerificationExpired(true);
                            setVerificationStatusMessage(
                              "Failed to resend verification email. Please try again.",
                            );
                          } finally {
                            setResendLoading(false);
                          }
                        }}
                      >
                        {resendCountdown > 0
                          ? `Resend in ${Math.floor(resendCountdown / 60)}:${String(resendCountdown % 60).padStart(2, "0")}`
                          : "Resend Verification Email"}
                      </LoadingButton>
                      <Typography variant="caption" color="text.secondary">
                        Didn't receive the email? Check your spam folder.
                      </Typography>
                    </Box>
                  </>
                ) : (
                  // Email verified - show reCAPTCHA and Submit
                  <>
                    <CheckCircleIcon
                      sx={{ fontSize: 60, color: "success.main", mb: 2 }}
                    />
                    <Typography variant="h5" gutterBottom color="success.main">
                      Email Verified!
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Please complete the verification below to finish your
                      registration.
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <ReCAPTCHA
                        key={`recaptcha-${recaptchaKey}`}
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA}
                        onChange={handleRecaptchaVerify}
                        onExpired={handleRecaptchaExpired}
                        onError={() => {
                          console.error("reCAPTCHA error occurred");
                          setRecaptchaError(
                            "reCAPTCHA error. Please refresh and try again.",
                          );
                        }}
                      />
                      {recaptchaError && (
                        <Typography variant="body2" color="error">
                          {recaptchaError}
                        </Typography>
                      )}
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        size="large"
                        sx={{ minWidth: 200, mt: 2 }}
                        loading={modalSubmitLoading}
                        disabled={!recaptchaVerified}
                        onClick={async () => {
                          setModalSubmitLoading(true);
                          try {
                            formik.setTouched({});
                            await formik.submitForm();
                          } catch (e) {
                            console.error("Registration failed:", e);
                            setModalSubmitLoading(false);
                          }
                        }}
                      >
                        Complete Registration
                      </LoadingButton>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
          <AdminLoginModal
            title="Registering as Admin"
            open={adminModalOpen}
            setOpen={setAdminModalOpen}
            onClose={() => setAdminModalOpen(false)}
            adminEmail={adminEmail}
            setAdminEmail={setAdminEmail}
            adminSecret={adminSecret}
            setAdminSecret={setAdminSecret}
          />
        </Box>
      )}
    </>
  );
};

export default NewRegister;
